// Continuous control-claim diff. Pure. No React, no broadcasts.
// GameBoard collects old/new claim maps during recomputeStaticAuras,
// hands them here for the diff, and applies the returned ops with
// the necessary animation + sync calls.
//
// Sorcery rule: "You control all X" (Returned King). While the source
// is in play AND active (not disabled), every matching card is
// controlled by the source's player. When the source leaves or
// becomes disabled, the claim releases and each card reverts to its
// original controller.

/**
 * @typedef {Object} ControlOp
 * @property {string} cardId
 * @property {'claim'|'release'} action
 * @property {boolean} newRotated - the rotated flag the card should end up with
 */

/**
 * Diff the current frame's wanted claim map against the previous
 * frame's. Returns the per-card actions GameBoard should apply.
 *
 * Each map key is a cardId; the value is the CLAIMING side's
 * rotated flag (true = p2, false = p1).
 *
 * For each card:
 *   - in newMap, not in oldMap            → action 'claim', snapshot _origRotated
 *   - in both, rotated mismatch           → action 'claim' (no snapshot)
 *   - in both, same side                  → no-op (filtered out)
 *   - in oldMap, not in newMap            → action 'release', restore _origRotated
 *
 * The action list is order-stable so callers can animate sequentially.
 *
 * @param {Map<string, boolean>} oldMap - cardId → claiming rotated
 * @param {Map<string, boolean>} newMap - cardId → claiming rotated
 * @param {(cardId: string) => Object|null} getCard - resolves cardInstance
 * @returns {ControlOp[]}
 */
export function diffControlClaims(oldMap, newMap, getCard) {
  const ops = [];

  for (const [cardId, claimRotated] of newMap) {
    const card = getCard(cardId);
    if (!card) continue;
    if (!card._controlClaimedBy) {
      // First-time claim. Snapshot the original side.
      card._origRotated = card.rotated;
    }
    card._controlClaimedBy = true;
    if (card.rotated !== claimRotated) {
      ops.push({ cardId, action: 'claim', newRotated: claimRotated });
    }
  }

  for (const [cardId] of oldMap) {
    if (newMap.has(cardId)) continue;
    const card = getCard(cardId);
    if (!card) continue;
    const orig = card._origRotated;
    delete card._controlClaimedBy;
    delete card._origRotated;
    if (orig !== undefined && card.rotated !== orig) {
      ops.push({ cardId, action: 'release', newRotated: orig });
    }
  }

  return ops;
}

/**
 * Snapshot the current control claims on the board so the next
 * recompute can diff against them. Pure. Just reads _controlClaimedBy.
 * Returns a Map<cardId, currentRotated>.
 */
export function snapshotControlClaims(meshes) {
  const out = new Map();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (card?._controlClaimedBy) out.set(card.id, card.rotated);
  }
  return out;
}
